import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import { GAME_SOUNDS } from '../../logic/minigames/sounds';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { animate, COLOURS, displayStyle, ease, prefersReducedMotion, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';
import { isRare, prizeText, wheelOrder, type WeeklyGiftPrize } from './weeklyGift';

export { prizeText, type WeeklyGiftPrize } from './weeklyGift';

/** The weekly gift board's art, looked up when it opens. */
export interface WeeklyGiftArt {
  floor: () => Texture | null;
  coin: () => Texture | null;
  gem: () => Texture | null;
  gift: () => Texture | null;
  /** The Lucky Wheel's painted rim, hub and pointer (null until loaded: then drawn). */
  rim: () => Texture | null;
  hub: () => Texture | null;
  pointer: () => Texture | null;
}

/** Segment fills, warm like the Lucky Wheel's; the rare prizes stand out in deep teal. */
const FILLS = [0xe0a020, 0xb83a2a, 0x2f7a34, 0xf3e3c0];
const RARE_FILL = 0x14606b;
/** The painted rim is drawn 2.28r across; labels stay inside 0.8r and outside the hub. */
const LABEL_SAFE = 0.8;
const LABEL_AT = 0.62;
const LABEL_HUB = 0.3;


/** This week's gift as the host sees it. */
export interface WeeklyGiftOffer {
  /** A gift box to open, or a wheel to spin (the post's link says which). */
  style: 'box' | 'wheel';
  /** Every prize, in wheel order. */
  prizes: readonly WeeklyGiftPrize[];
  /** This week's prize when it has already been opened; null while it waits. */
  taken: WeeklyGiftPrize | null;
  /** Opens it on the server; the prize, or null when nothing was credited. */
  claim: () => Promise<WeeklyGiftPrize | null>;
  /** Coins or gems credited fly from `from` into their counter. */
  fly: (from: { x: number; y: number }, prize: WeeklyGiftPrize) => Promise<void>;
}

type Phase = 'offer' | 'opening' | 'shown' | 'taken';

/**
 * The weekly gift from the Facebook Page's links (producer 2026-11-04: "a
 * mix of spin the wheel or here's your gift"), over the slot on a board.
 * The server picks the prize; the box opens on it and the wheel lands on
 * it. Once this week's is opened the board says what it was and when the
 * next one comes. "See the chances" lists every prize with its odds.
 */
export class WeeklyGiftBoard extends BoardDialog {
  private readonly line: Text;
  private readonly glow = new Sprite(glowTexture());
  private readonly box = new Sprite();
  private readonly wheel = new Container();
  /** Wedges, pegs and labels: the part that turns. */
  private readonly wheelDisc = new Container();
  private readonly wedges = new Graphics();
  private readonly pegs = new Graphics();
  private readonly labels = new Container();
  private readonly landed = new Graphics();
  private readonly shading = new Graphics();
  private readonly rimSprite = new Sprite();
  private readonly hubSprite = new Sprite();
  private readonly pointerSprite = new Sprite();
  private readonly drawnRim = new Graphics();
  private readonly drawnPointer = new Graphics();
  private readonly pointerGlow = new Sprite(glowTexture());
  /** Prize indexes round the wheel, clockwise from the top. */
  private order: number[] = [];
  private lastPeg = 0;
  private readonly prizeLine: Text;
  private readonly prizeIcon = new Sprite();
  private readonly mainButton: BrassButton;
  private readonly chancesButton: Text;
  private readonly chances: Text;
  private readonly after: Text;
  private offer: WeeklyGiftOffer | null = null;
  private phase: Phase = 'offer';
  private showChances = false;
  private done: (() => void) | null = null;
  private arranging = false;
  private wheelRadius = 120;

  constructor(private readonly art: WeeklyGiftArt) {
    super({ title: 'YOUR FREE GIFT', floor: art.floor(), maxWidth: 420, titleSize: 18 });
    this.line = new Text({ text: '', style: { ...uiStyle(15, COLOURS.cream), align: 'center', wordWrap: true } });
    this.line.anchor.set(0.5, 0);
    this.glow.anchor.set(0.5);
    this.glow.blendMode = 'add';
    this.glow.tint = 0xffc457;
    this.box.anchor.set(0.5);
    this.wheelDisc.addChild(this.wedges, this.landed, this.labels, this.pegs);
    for (const sp of [this.rimSprite, this.hubSprite, this.pointerGlow]) sp.anchor.set(0.5);
    this.pointerSprite.anchor.set(0.5, 0.15);
    this.pointerGlow.blendMode = 'add';
    this.pointerGlow.tint = 0xffe08a;
    this.pointerGlow.visible = false;
    this.wheel.addChild(this.wheelDisc, this.shading, this.drawnRim, this.rimSprite, this.hubSprite, this.pointerGlow, this.drawnPointer, this.pointerSprite);
    this.prizeLine = new Text({ text: '', style: displayStyle(34) });
    this.prizeLine.anchor.set(0.5);
    this.prizeIcon.anchor.set(0.5);
    this.mainButton = new BrassButton({ label: 'OPEN', kind: 'gold', size: 18, onTap: () => void this.act() });
    this.chancesButton = new Text({ text: 'See the chances', style: { ...uiStyle(13, COLOURS.cream, '600') } });
    this.chancesButton.anchor.set(0.5, 0);
    this.chancesButton.eventMode = 'static';
    this.chancesButton.cursor = 'pointer';
    this.chancesButton.on('pointertap', () => {
      this.showChances = !this.showChances;
      this.arrange();
    });
    this.chances = new Text({ text: '', style: { ...uiStyle(13, COLOURS.cream, '500'), align: 'center', lineHeight: 19 } });
    this.chances.anchor.set(0.5, 0);
    this.after = new Text({ text: '', style: { ...uiStyle(15, COLOURS.cream), align: 'center', wordWrap: true } });
    this.after.anchor.set(0.5, 0);
    this.content.addChild(
      this.line, this.glow, this.box, this.wheel, this.prizeLine, this.prizeIcon,
      this.mainButton, this.chancesButton, this.chances, this.after,
    );
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): { open: boolean; style: string; phase: Phase; buttons: string[]; prize: string; line: string; after: string } {
    return {
      open: this.visible,
      style: this.offer?.style ?? '',
      phase: this.phase,
      buttons: this.visible && this.mainButton.visible ? [this.mainButton.labelText] : [],
      prize: this.prizeLine.visible ? this.prizeLine.text : '',
      line: this.line.text,
      after: this.after.visible ? this.after.text : '',
    };
  }

  /** Press the board's button by its label (browser tests). */
  press(label: string): boolean {
    if (!this.visible || !this.mainButton.visible || this.mainButton.labelText !== label) return false;
    void this.act();
    return true;
  }

  /** Offer this week's gift (or say it's been opened); resolves when the board has closed. */
  async play(offer: WeeklyGiftOffer): Promise<void> {
    this.offer = offer;
    this.showChances = false;
    this.setFloor(this.art.floor());
    this.chances.text = offer.prizes
      .map((p) => `${prizeText(p)}: ${p.chance.toLocaleString('en-GB', { maximumFractionDigits: 2 })}%`)
      .join('\n');
    if (offer.taken) {
      this.phase = 'taken';
      this.setTitle('THIS WEEK\'S GIFT');
      this.line.text = 'You\'ve opened this week\'s gift.';
      this.showPrize(offer.taken);
      this.after.text = 'A new one opens on Monday.';
      this.mainButton.setLabel('OK');
    } else {
      this.phase = 'offer';
      this.setTitle(offer.style === 'wheel' ? 'SPIN FOR A GIFT' : 'YOUR FREE GIFT');
      this.line.text = offer.style === 'wheel'
        ? 'One free spin a week, on us.'
        : 'A gift from Goodtime, once a week.';
      this.prizeLine.visible = this.prizeIcon.visible = false;
      this.after.text = '';
      this.mainButton.setLabel(offer.style === 'wheel' ? 'SPIN' : 'OPEN');
    }
    this.mainButton.visible = true;
    this.mainButton.setEnabled(true);
    this.box.visible = offer.style === 'box' && !offer.taken;
    this.glow.visible = this.box.visible;
    this.wheel.visible = offer.style === 'wheel' && !offer.taken;
    const gift = this.art.gift();
    if (gift) this.box.texture = gift;
    if (this.wheel.visible) this.drawWheel(offer.prizes);
    this.arrange();
    const closed = new Promise<void>((resolve) => (this.done = resolve));
    await this.open();
    if (this.box.visible) this.bob();
    if (this.wheel.visible) this.drift();
    return closed;
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  private async act(): Promise<void> {
    const offer = this.offer;
    if (!offer) return;
    if (this.phase === 'taken' || this.phase === 'shown') {
      await this.finish();
      return;
    }
    if (this.phase !== 'offer') return;
    this.phase = 'opening';
    this.mainButton.setEnabled(false);
    this.showChances = false;
    this.arrange();
    const prize = await offer.claim();
    if (!prize) {
      await this.finish();
      return;
    }
    if (offer.style === 'wheel') await this.spinTo(prize, offer.prizes);
    else await this.burst();
    this.phase = 'shown';
    this.line.text = offer.style === 'wheel' ? 'The wheel says:' : 'Inside your gift:';
    this.showPrize(prize);
    this.after.text = `Added to your ${prize.gems > 0 ? 'gems' : 'coins'}. A new gift opens on Monday.`;
    this.mainButton.setLabel('LOVELY');
    this.mainButton.setEnabled(true);
    this.box.visible = this.glow.visible = false;
    this.wheel.visible = false;
    this.arrange();
    const at = { x: this.contentWidth / 2, y: this.prizeLine.y };
    void this.celebrate(at);
    if (isRare(prize, offer.prizes)) setTimeout(() => void this.celebrate(at), 450);
    await offer.fly(this.prizeIcon.getGlobalPosition(), prize);
  }

  private showPrize(p: WeeklyGiftPrize): void {
    this.prizeLine.text = `+${(p.gems > 0 ? p.gems : p.coins).toLocaleString('en-GB')}`;
    const icon = p.gems > 0 ? this.art.gem() : this.art.coin();
    if (icon) this.prizeIcon.texture = icon;
    this.prizeLine.visible = true;
    this.prizeIcon.visible = icon !== null;
  }

  private async finish(): Promise<void> {
    const done = this.done;
    this.done = null;
    await this.close();
    done?.();
  }

  /** The box bobs while it waits. */
  private bob(): void {
    if (prefersReducedMotion()) return;
    const loop = () => {
      if (!this.visible || this.phase !== 'offer' || this.destroyed) return;
      void animate(1400, (t) => {
        const s = Math.sin(t * Math.PI);
        this.box.rotation = 0.06 * Math.sin(t * Math.PI * 2);
        this.glow.alpha = 0.55 + 0.45 * s;
      }).then(loop);
    };
    loop();
  }

  /** The box shakes, swells and pops. */
  private async burst(): Promise<void> {
    if (prefersReducedMotion()) return;
    const base = this.box.scale.x;
    await animate(500, (t) => {
      this.box.rotation = 0.18 * Math.sin(t * Math.PI * 8) * (1 - t);
    });
    await animate(260, (t) => {
      this.box.scale.set(base * (1 + 0.35 * ease.outCubic(t)));
      this.box.alpha = 1 - t;
      this.glow.alpha = 1;
      this.glow.scale.set(this.glow.scale.x * (1 + 0.02));
    });
    this.box.scale.set(base);
    this.box.alpha = 1;
    this.box.rotation = 0;
  }

  /** The waiting wheel turns slowly, inviting a spin. */
  private drift(): void {
    if (prefersReducedMotion()) return;
    let last = performance.now();
    const loop = () => {
      if (!this.visible || this.phase !== 'offer' || this.destroyed) return;
      const now = performance.now();
      this.wheelDisc.rotation += (0.25 * (now - last)) / 1000;
      last = now;
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  /**
   * A short pull back, five turns easing out with a tick at every peg, a
   * little past and back, and the prize under the pointer; then it glows.
   */
  private async spinTo(prize: WeeklyGiftPrize, prizes: readonly WeeklyGiftPrize[]): Promise<void> {
    const n = this.order.length || prizes.length;
    const step = (Math.PI * 2) / n;
    const slot = Math.max(0, this.order.findIndex((i) => prizes[i]?.index === prize.index));
    const from = this.wheelDisc.rotation;
    // Segment `slot` sits under the pointer when the disc is turned back by `slot` steps.
    const rest = -slot * step + (Math.random() - 0.5) * step * 0.5;
    const turns = Math.PI * 2 * 5;
    const base = from - (((from - rest) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const to = base + turns;
    if (prefersReducedMotion()) {
      this.wheelDisc.rotation = to;
    } else {
      await animate(260, (t) => (this.wheelDisc.rotation = from - 0.12 * Math.sin(t * Math.PI * 0.5)));
      // Eases a little past the prize, then settles back onto it.
      const start = this.wheelDisc.rotation;
      const past = to + step * 0.2;
      await animate(4400, (t) => {
        this.wheelDisc.rotation = start + (past - start) * (1 - Math.pow(1 - t, 4));
        this.tickPegs(step);
      });
      await animate(380, (t) => {
        this.wheelDisc.rotation = past + (to - past) * ease.outCubic(t);
        this.tickPegs(step);
      });
    }
    AudioBus.shared.play(GAME_SOUNDS.wheelLand, { volume: 0.8 });
    this.lightUp(slot, step);
    await new Promise((resolve) => setTimeout(resolve, prefersReducedMotion() ? 200 : 900));
  }

  /** A tick, and a flick of the pointer, each time a peg goes past it. */
  private tickPegs(step: number): void {
    const peg = Math.floor((this.wheelDisc.rotation + step / 2) / step);
    if (peg === this.lastPeg) return;
    this.lastPeg = peg;
    AudioBus.shared.play(GAME_SOUNDS.wheelTick, { volume: 0.35 });
    if (prefersReducedMotion()) return;
    void animate(120, (t) => {
      const k = Math.sin(t * Math.PI) * 0.22;
      this.pointerSprite.rotation = this.drawnPointer.rotation = -k;
    });
  }

  /** The landed segment and the pointer glow. */
  private lightUp(slot: number, step: number): void {
    const r = this.wheelRadius;
    const a0 = -Math.PI / 2 + (slot - 0.5) * step;
    this.landed.clear().moveTo(0, 0).arc(0, 0, r, a0, a0 + step).lineTo(0, 0).fill({ color: 0xfff4c0, alpha: 0.45 });
    this.pointerGlow.visible = true;
    if (prefersReducedMotion()) return;
    void animate(900, (t) => {
      this.landed.alpha = 0.4 + 0.6 * Math.abs(Math.sin(t * Math.PI * 2));
      this.pointerGlow.alpha = Math.sin(t * Math.PI);
    });
  }

  private drawWheel(prizes: readonly WeeklyGiftPrize[]): void {
    const r = this.wheelRadius;
    this.order = wheelOrder(prizes);
    const n = this.order.length;
    const step = (Math.PI * 2) / n;
    this.wheelDisc.rotation = 0;
    this.wedges.clear();
    this.pegs.clear();
    this.landed.clear();
    this.pointerGlow.visible = false;
    for (const child of this.labels.removeChildren()) child.destroy({ children: true });
    const coin = this.art.coin();
    const gem = this.art.gem();
    this.order.forEach((pi, i) => {
      const p = prizes[pi]!;
      const rare = isRare(p, prizes);
      const a0 = -Math.PI / 2 + (i - 0.5) * step;
      const fill = rare ? RARE_FILL : FILLS[i % FILLS.length]!;
      this.wedges.moveTo(0, 0).arc(0, 0, r, a0, a0 + step).lineTo(0, 0).fill({ color: fill }).stroke({ color: 0x5a3210, width: 2 });
      // A brass peg on the rim at each wedge's edge.
      this.pegs.circle(Math.cos(a0) * r * 0.82, Math.sin(a0) * r * 0.82, Math.max(3, r * 0.032))
        .fill({ color: COLOURS.brassLight })
        .stroke({ color: COLOURS.ink, width: 1.5 });
      const mid = a0 + step / 2;
      const light = fill === 0xf3e3c0 || fill === 0xe0a020;
      const label = new Container();
      const text = new Text({
        text: (p.gems > 0 ? p.gems : p.coins).toLocaleString('en-GB'),
        style: displayStyle(Math.max(13, Math.round(r * (rare ? 0.17 : 0.15))), light ? 0x3a1a00 : COLOURS.gold),
      });
      if (light) text.style.stroke = { color: 0xfff4d0, width: 2, join: 'round' };
      text.anchor.set(0.5);
      text.y = -r * 0.07;
      const iconTex = p.gems > 0 ? gem : coin;
      label.addChild(text);
      if (iconTex) {
        const icon = new Sprite(iconTex);
        icon.anchor.set(0.5);
        const size = r * (rare ? 0.26 : 0.21);
        icon.scale.set(size / Math.max(1, iconTex.width, iconTex.height));
        icon.y = r * 0.12;
        label.addChild(icon);
      }
      label.rotation = mid + Math.PI / 2;
      this.placeLabel(label, mid, r);
      this.labels.addChild(label);
      if (rare) {
        const sparkle = new Sprite(glowTexture());
        sparkle.anchor.set(0.5);
        sparkle.blendMode = 'add';
        sparkle.tint = 0xfff0a0;
        sparkle.alpha = 0.7;
        sparkle.width = sparkle.height = r * 0.5;
        sparkle.position.copyFrom(label.position);
        this.labels.addChildAt(sparkle, 0);
      }
    });
    this.shading.clear().circle(0, 0, r - 3).stroke({ color: 0x1e0e02, alpha: 0.3, width: 6 });
    const rim = this.art.rim();
    this.rimSprite.visible = rim !== null;
    this.drawnRim.clear();
    if (rim) {
      this.rimSprite.texture = rim;
      this.rimSprite.width = this.rimSprite.height = r * 2.28;
    } else {
      this.drawnRim.circle(0, 0, r).stroke({ color: COLOURS.brass, width: 8 });
    }
    const hub = this.art.hub();
    this.hubSprite.visible = hub !== null;
    if (hub) {
      this.hubSprite.texture = hub;
      this.hubSprite.width = this.hubSprite.height = r * 0.5;
    } else {
      this.drawnRim.circle(0, 0, r * 0.16).fill({ color: COLOURS.woodDark }).stroke({ color: COLOURS.brassLight, width: 3 });
    }
    const pointer = this.art.pointer();
    this.pointerSprite.visible = pointer !== null;
    this.drawnPointer.clear();
    if (pointer) {
      this.pointerSprite.texture = pointer;
      this.pointerSprite.width = this.pointerSprite.height = r * 0.32;
      this.pointerSprite.position.set(0, -r - r * 0.16);
    } else {
      this.drawnPointer.poly([-14, -r - 18, 14, -r - 18, 0, -r + 8]).fill({ color: COLOURS.gold }).stroke({ color: COLOURS.ink, width: 2 });
    }
    this.pointerGlow.position.set(0, -r * 0.62);
    this.pointerGlow.width = this.pointerGlow.height = r * 0.9;
  }

  /** A label on its wedge's centre line, inside the rim and outside the hub (as the Lucky Wheel). */
  private placeLabel(label: Container, mid: number, r: number): void {
    const b = label.getLocalBounds();
    const safe = r * LABEL_SAFE;
    let scale = 1;
    let d = r * LABEL_AT;
    for (let k = 0; k < 8; k++) {
      const half = Math.max(-b.minX, b.maxX) * scale;
      d = Math.min(r * LABEL_AT, Math.sqrt(Math.max(0, safe * safe - half * half)) + b.minY * scale);
      if (d - b.maxY * scale >= r * LABEL_HUB) break;
      scale *= 0.92;
    }
    label.scale.set(scale);
    label.position.set(Math.cos(mid) * d, Math.sin(mid) * d);
  }

  private arrange(): void {
    if (this.arranging || !this.after) return;
    this.arranging = true;
    const w = this.contentWidth;
    this.line.style.wordWrapWidth = w - 8;
    this.line.position.set(w / 2, 0);
    let y = this.line.height + 14;
    if (this.box.visible) {
      const size = Math.min(150, w * 0.45);
      const k = size / Math.max(1, this.box.texture.width, this.box.texture.height);
      this.box.scale.set(k);
      this.box.position.set(w / 2, y + size / 2);
      this.glow.width = this.glow.height = size * 1.9;
      this.glow.position.copyFrom(this.box.position);
      y += size + 16;
    }
    if (this.wheel.visible) {
      const r = Math.round(Math.min(140, (w - 12) / 2.28));
      if (Math.abs(r - this.wheelRadius) > 1 && this.offer) {
        this.wheelRadius = r;
        this.drawWheel(this.offer.prizes);
      }
      this.wheel.position.set(w / 2, y + this.wheelRadius * 1.14 + 22);
      y += this.wheelRadius * 2.28 + 32;
    }
    if (this.prizeLine.visible) {
      const coinSize = 40;
      const k = coinSize / Math.max(1, this.prizeIcon.texture.width, this.prizeIcon.texture.height);
      this.prizeIcon.scale.set(k);
      const rowW = this.prizeLine.width + (this.prizeIcon.visible ? coinSize + 8 : 0);
      this.prizeLine.position.set((w - rowW) / 2 + this.prizeLine.width / 2, y + 24);
      this.prizeIcon.position.set((w - rowW) / 2 + this.prizeLine.width + 8 + coinSize / 2, y + 24);
      y += 56;
    }
    this.after.visible = this.after.text !== '';
    if (this.after.visible) {
      this.after.style.wordWrapWidth = w - 8;
      this.after.position.set(w / 2, y);
      y += this.after.height + 12;
    }
    const bw = Math.min(w, 220);
    this.mainButton.resize(bw, 48);
    this.mainButton.position.set((w - bw) / 2, y);
    y += 56;
    this.chancesButton.visible = this.phase === 'offer';
    this.chances.visible = this.chancesButton.visible && this.showChances;
    if (this.chancesButton.visible) {
      this.chancesButton.text = this.showChances ? 'Hide the chances' : 'See the chances';
      this.chancesButton.position.set(w / 2, y);
      y += this.chancesButton.height + 6;
      if (this.chances.visible) {
        this.chances.position.set(w / 2, y);
        y += this.chances.height + 6;
      }
    }
    this.setContentHeight(y);
    this.arranging = false;
  }
}
